import { NavLink } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { cn } from '@/lib/cn';
import { NAV } from '@/app/nav';
import { Logo } from './Logo';
import { useAlerts, useRecommendations, useSession } from '@/lib/hooks';

export function Sidebar({
  collapsed,
  onToggle,
  className,
}: {
  collapsed: boolean;
  onToggle: () => void;
  className?: string;
}) {
  const alerts = useAlerts();
  const recs = useRecommendations();
  const badges = {
    alerts: alerts.data ? alerts.data.counts.critico + alerts.data.counts.alerta : undefined,
    recommendations: recs.data?.counts.critico,
  };

  return (
    <aside
      className={cn(
        'flex h-full flex-col border-r border-hairline bg-panel transition-[width] duration-200',
        collapsed ? 'w-[72px]' : 'w-[248px]',
        className,
      )}
    >
      <div className="flex items-center justify-between px-4 py-4">
        <Logo compact={collapsed} />
        <button
          onClick={onToggle}
          className={cn(
            'grid h-7 w-7 shrink-0 place-items-center rounded-lg text-ink-faint hover:bg-hover hover:text-ink',
            collapsed && 'absolute',
          )}
          aria-label="Colapsar menú"
        >
          <ChevronLeft className={cn('h-4 w-4 transition-transform', collapsed && 'rotate-180')} />
        </button>
      </div>

      <nav className="flex-1 space-y-5 overflow-y-auto px-3 py-2">
        {NAV.map((section) => (
          <div key={section.title}>
            {!collapsed && <p className="section-label px-3 pb-2">{section.title}</p>}
            <ul className="space-y-0.5">
              {section.items.map((item) => {
                const badge = item.badgeKey ? badges[item.badgeKey] : undefined;
                return (
                  <li key={item.path}>
                    <NavLink
                      to={item.path}
                      end={item.path === '/'}
                      className={({ isActive }) =>
                        cn(
                          'group flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-[13px] font-medium transition-colors',
                          isActive
                            ? 'bg-primary text-white shadow-sm'
                            : 'text-ink-muted hover:bg-hover hover:text-ink-strong',
                          collapsed && 'justify-center px-0',
                        )
                      }
                      title={collapsed ? item.label : undefined}
                    >
                      <item.icon className="h-[18px] w-[18px] shrink-0" strokeWidth={2} />
                      {!collapsed && <span className="flex-1 truncate">{item.label}</span>}
                      {badge != null && badge > 0 && (
                        <span
                          className={cn(
                            'grid h-5 min-w-5 place-items-center rounded-full bg-danger px-1 text-2xs font-bold text-white',
                            collapsed && 'absolute ml-7 -mt-5 h-4 min-w-4',
                          )}
                        >
                          {badge}
                        </span>
                      )}
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      <UserCard collapsed={collapsed} />
    </aside>
  );
}

function UserCard({ collapsed }: { collapsed: boolean }) {
  // Sale de la sesión (cuenta del B2B); antes estaba hardcodeado "Carlos Ruiz · Jefe de Compras".
  const user = useSession().data?.user;
  const parts = (user?.name ?? '').trim().split(/\s+/).filter(Boolean);
  const iniciales = parts.length
    ? (parts[0]![0]! + (parts.length > 1 ? parts[parts.length - 1]![0]! : '')).toUpperCase()
    : '—';

  return (
    <div className="flex items-center gap-3 border-t border-hairline px-4 py-3">
      <div className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-primary text-xs font-bold text-white">
        {iniciales}
      </div>
      {!collapsed && (
        <div className="min-w-0 leading-tight">
          <p className="truncate text-sm font-semibold text-ink-strong">{user?.name ?? '—'}</p>
          <p className="truncate text-2xs capitalize text-ink-muted">{user?.role ?? ''}</p>
        </div>
      )}
    </div>
  );
}
